import type { CSSProperties } from 'react';
import type { ImageField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';
import {
  StepBody,
  StepTitle,
} from '@/components/redesign/NumberedIconStepCard';
import type { ParagraphStep } from './types';

/**
 * Grid config for the paragraph-steps row, colocated with the card that needs
 * it. The desktop column count comes from how many cards there were authored
 * (see `paragraphStepsGridStyle`) so the row always divides the available
 * width evenly instead of leaving a stretched orphan. 28px gap (Figma).
 */
export const PARAGRAPH_STEPS_GRID_CLASS =
  'grid-cols-1 gap-7 md:grid-cols-2 lg:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]';

/**
 * Past four across the cards get too narrow to read, so the row wraps instead;
 * a wrapped grid card keeps its column width rather than stretching.
 */
const MAX_COLUMNS = 4;

/** Feeds `PARAGRAPH_STEPS_GRID_CLASS` — pass as the grid container's style. */
export const paragraphStepsGridStyle = (count: number): CSSProperties =>
  ({ '--cols': Math.min(Math.max(count, 1), MAX_COLUMNS) }) as CSSProperties;

/** Figma: 24px padding, 28px internal gap, 10px radius (from the shell). */
const CARD_CLASS = 'min-w-0 gap-7 overflow-hidden p-6 text-white';

/**
 * The card fill, rebuilt from the Figma layer stack (bottom to top): the brand
 * green gradient, a dark wash in the top-right corner, a flat #444444 at 63%,
 * and a monotone noise grain. The #444444 wash is what mutes the brand green
 * into the card's olive tone — #05f36c under it computes to #2d8553.
 *
 * Listed here topmost-first, the order `background-image` expects.
 */
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.09'/%3E%3C/svg%3E\")";

const TINT = 'linear-gradient(rgba(68, 68, 68, 0.63), rgba(68, 68, 68, 0.63))';

const CORNER_SHADE =
  'radial-gradient(90% 130% at 100% 0%, rgba(0, 38, 28, 0.28) 0%, rgba(0, 38, 28, 0) 65%)';

/**
 * Stands in for Gradient@300x.png. The stops are derived from the Figma card
 * by inverting the #444444 tint above — the rendered olive band #35845c to
 * #27614a resolves back to this green ramp.
 */
const BRAND_GRADIENT =
  'linear-gradient(115deg, #1cf185 0%, #06c46f 55%, #009254 100%)';

/** Noise tiles at its own size; every other layer covers the card. */
const LAYER_SIZES = 'auto, cover, cover, cover';
const LAYER_REPEATS = 'repeat, no-repeat, no-repeat, no-repeat';

type Props = {
  step: ParagraphStep;
  index: number;
  /** One image for every card in the row — there is no per-card background. */
  backgroundImage?: ImageField | null;
};

/**
 * Card for the `redesignParagraphSteps` layout: a numbered badge, a bold title,
 * and the copy set in a white box. The background is shared across the row, so
 * it arrives as a prop instead of coming off the step itself.
 */
export function ParagraphStepCard({ step, index, backgroundImage }: Props) {
  // A CSS background keeps the card a single in-flow element: no absolutely
  // positioned image to stack the content above, and the radius clips it.
  // An uploaded image replaces only the gradient layer — the tint and grain
  // sit above it in Figma, so they stay either way.
  const base = isFilled.image(backgroundImage)
    ? `url(${backgroundImage.url})`
    : BRAND_GRADIENT;

  const style: CSSProperties = {
    backgroundImage: [NOISE, TINT, CORNER_SHADE, base].join(', '),
    backgroundSize: LAYER_SIZES,
    backgroundRepeat: LAYER_REPEATS,
    backgroundPosition: 'center',
  };

  return (
    <HorizontalContentCard className={CARD_CLASS} style={style}>
      <span className="w-fit border-l border-[#05f273] py-1 pl-2 text-[12px] font-semibold leading-[1.5] text-white">
        {step.step_number || String(index + 1)}
      </span>

      <StepTitle
        field={step.step_title}
        className="text-[20px] font-bold leading-[1.5] text-white md:text-[24px]"
      />

      {/* Figma hugs this box to its copy; 80% keeps that proportion without
          going ragged when cards hold different amounts of text. */}
      <div className="w-full rounded-[10px] bg-white p-5 sm:w-4/5">
        <StepBody
          field={step.step_description}
          className="text-[16px] font-light leading-[1.5] text-[#262626] md:text-[18px]"
          strongClassName="font-semibold"
        />
      </div>
    </HorizontalContentCard>
  );
}
